import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

/* Espelha `PurchaseOrderItemDto` do backend, campo a campo. O tipo anterior
   inventava `productName` e `unitPrice`; o DTO tem `description` e
   `unitValue`, e as duas colunas do detalhe apareciam vazias. */
export interface PurchaseOrderItem {
  id: string;
  productId: string;
  description: string;
  sku: string;
  ncmCode: string | null;
  unit: string;
  quantity: number;
  orderedQuantity: number;
  receivedQuantity: number;
  unitValue: number;
  discountValue: number;
  discountPercent: number;
  /** Valores (R$) por tributo, ex.: `{ ICMS: 4050, IPI: 1125 }`. */
  taxValues: Record<string, number>;
  total: number;
}

export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'IN_TRANSIT'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVED'
  | 'REJECTED'
  | 'CANCELLED';

export interface PurchaseOrder {
  id: string;
  number: string;
  vendorId: string;
  vendorName: string;
  vendorDocument: string;
  status: PurchaseOrderStatus;
  items: PurchaseOrderItem[];
  expectedDate: string | null;
  freightValue: number;
  discountValue: number;
  subtotal: number;
  total: number;
  notes: string | null;
  requestedBy: string;
  approvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  receivedAt: string | null;
  approvedAt: string | null;
}

export interface PurchaseOrderInput {
  vendorId: string;
  items: {
    productId: string;
    description?: string;
    quantity: number;
    unitValue: number;
    /** Valores em R$ por tributo — e assim que o dominio guarda. */
    taxValues?: Record<string, number>;
  }[];
  expectedDate?: string;
  notes?: string;
}

/** Espelha `GoodsReceiptStatus` do dominio. */
export type GoodsReceiptStatus = 'DRAFT' | 'COMPLETED' | 'CANCELLED';

/** Espelha `GoodsReceiptItemDto`, campo a campo. */
export interface GoodsReceiptItem {
  id: string;
  purchaseOrderItemId: string;
  productId: string;
  productName: string;
  sku: string;
  /** Quantidade do pedido. Era lida como `orderedQuantity`, que nao existe. */
  expectedQuantity: number;
  receivedQuantity: number;
  unitCost: number;
  totalCost: number;
  lotNumber: string | null;
  serialNumber: string | null;
  expirationDate: string | null;
  notes: string | null;
  status: string;
  rejectionReason: string | null;
}

/**
 * Espelha `GoodsReceiptDto`, campo a campo.
 *
 * Nao tem `orderId`, `orderNumber` nem `receiptDate`: o recebimento tem
 * `receiptNumber` (a identidade dele), `purchaseOrderId` (so o id do pedido —
 * o numero do pedido nao e publicado), `documentDate` (a data da nota) e
 * `createdAt`.
 */
export interface GoodsReceipt {
  id: string;
  tenantId: string;
  companyId: string;
  branchId: string;
  receiptNumber: string;
  status: GoodsReceiptStatus;
  purchaseOrderId: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  items: GoodsReceiptItem[];
  documentNumber: string | null;
  documentDate: string | null;
  freightCost: number;
  otherCosts: number;
  notes: string | null;
  receivedById: string;
  receivedBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Espelha `CreateGoodsReceiptItemDto`. */
export interface GoodsReceiptItemInput {
  purchaseOrderItemId: string;
  productId: string;
  productName: string;
  expectedQuantity: number;
  unitCost: number;
  lotNumber?: string;
  serialNumber?: string;
  expirationDate?: string;
  notes?: string;
}

/**
 * Espelha `CreateGoodsReceiptDto`.
 *
 * O POST cria o recebimento em DRAFT com as quantidades **esperadas**. O que
 * chegou de fato entra depois, por `PATCH /:id/receive` — sao dois passos no
 * dominio, e a tela antiga tentava fazer os dois em um pedido que o servidor
 * recusava com 400.
 */
export interface GoodsReceiptInput {
  purchaseOrderId: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  items: GoodsReceiptItemInput[];
  documentNumber?: string;
  documentDate?: string;
  freightCost?: number;
  otherCosts?: number;
  notes?: string;
}

/** Espelha `ReceiveGoodsDto`: o segundo passo. */
export interface ReceiveGoodsInput {
  items: { itemId: string; quantity: number }[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/* ── RFQ Types ── */

/* Espelha `RfqItemDto`. Antes declarava `productName` e `specifications`,
   que o backend nunca mandou: a coluna Produto da tela de cotacoes vinha em
   branco e a de especificacao vinha sempre com tracinho. */
export interface RfqItem {
  id: string;
  productId: string;
  description: string;
  sku: string;
  ncmCode: string | null;
  unit: string;
  quantity: number;
}

export interface RfqResponseItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
  deliveryDays: number;
}

export interface RfqResponse {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorDocument: string;
  vendorContactName?: string;
  vendorContactEmail?: string;
  items: RfqResponseItem[];
  total: number;
  deliveryEstimateDays: number;
  validityDays: number;
  paymentTerms?: string;
  notes?: string;
  isWinner: boolean;
  submittedAt: string;
  expiresAt: string;
}

export interface Rfq {
  id: string;
  number: string;
  title: string;
  description?: string;
  status: 'DRAFT' | 'SENT' | 'QUOTED' | 'AWARDED' | 'EXPIRED' | 'CANCELLED';
  items: RfqItem[];
  deadline: string;
  expectedDeliveryDate?: string;
  vendorResponses: RfqResponse[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  awardedToVendorName?: string;
  awardedResponseId?: string;
  awardedAt?: string;
  cancellationReason?: string;
}

export interface CreateRfqInput {
  title: string;
  description?: string;
  items: { productId: string; productName: string; quantity: number; unit: string; specifications?: string }[];
  deadline: string;
  expectedDeliveryDate?: string;
  awardCriteria?: string;
  paymentTerms?: string;
  paymentCondition?: string;
  freightResponsibility?: string;
  internalNotes?: string;
}

export interface SubmitRfqResponseInput {
  vendorId: string;
  vendorName: string;
  vendorDocument: string;
  vendorContactName?: string;
  vendorContactEmail?: string;
  vendorContactPhone?: string;
  items: { rfqItemIndex: number; productId: string; productName: string; quantity: number; unitPrice: number; deliveryDays: number }[];
  total: number;
  deliveryEstimateDays: number;
  validityDays: number;
  paymentTerms?: string;
  paymentCondition?: string;
  notes?: string;
}

export interface AwardRfqInput {
  responseId: string;
}

export interface CancelRfqInput {
  reason: string;
}

/* ── RFQ Hooks ── */

export function useRfqs(params?: {
  page?: number;
  limit?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: [...queryKeys.procurement.rfqs(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Rfq>>('/procurement/rfqs', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
          ...(params?.status && params.status !== 'ALL'
            ? { status: params.status }
            : {}),
        },
      }),
  });
}

export function useRfq(id: string) {
  return useQuery({
    queryKey: queryKeys.procurement.rfq(id),
    queryFn: () => apiClient.get<Rfq>(`/procurement/rfqs/${id}`),
    enabled: !!id,
  });
}

export function useCreateRfq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRfqInput) =>
      apiClient.post<Rfq>('/procurement/rfqs', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.rfqs() });
    },
  });
}

export function useSendRfq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<Rfq>(`/procurement/rfqs/${id}/send`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.rfqs() });
    },
  });
}

export function useSubmitRfqResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rfqId, input }: { rfqId: string; input: SubmitRfqResponseInput }) =>
      apiClient.post<Rfq>(`/procurement/rfqs/${rfqId}/responses`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.rfqs() });
    },
  });
}

export function useAwardRfq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rfqId, input }: { rfqId: string; input: AwardRfqInput }) =>
      apiClient.post<Rfq>(`/procurement/rfqs/${rfqId}/award`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.rfqs() });
    },
  });
}

export function useCancelRfq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rfqId, input }: { rfqId: string; input: CancelRfqInput }) =>
      apiClient.post<Rfq>(`/procurement/rfqs/${rfqId}/cancel`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.rfqs() });
    },
  });
}

/* ── Purchase Order Hooks ── */

export function usePurchaseOrders(params?: {
  page?: number;
  limit?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: [...queryKeys.procurement.orders(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<PurchaseOrder>>(
        '/procurement/purchase-orders',
        {
          params: {
            page: String(params?.page ?? 1),
            limit: String(params?.limit ?? 20),
            ...(params?.status && params.status !== 'ALL'
              ? { status: params.status }
              : {}),
          },
        },
      ),
  });
}

export function usePurchaseOrder(id: string) {
  return useQuery({
    queryKey: [...queryKeys.procurement.orders(), id],
    queryFn: () =>
      apiClient.get<PurchaseOrder>(
        `/procurement/purchase-orders/${id}`,
      ),
    enabled: !!id,
  });
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PurchaseOrderInput) =>
      apiClient.post<PurchaseOrder>('/procurement/purchase-orders', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.orders() });
    },
  });
}

/* Os lookups de fornecedor e de produto vivem em `shared/lookups`: tres telas
   usam os mesmos (pedido de compra, contas a pagar, contas a receber). */
export { useSupplierLookup, useProductLookup } from '../../shared/lookups/useParties';

export function useApprovePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<PurchaseOrder>(
        `/procurement/purchase-orders/${id}/approve`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.orders() });
    },
  });
}

export function useCancelPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<PurchaseOrder>(
        `/procurement/purchase-orders/${id}/cancel`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.orders() });
    },
  });
}

/* ── Goods Receipt Hooks ── */

export function useGoodsReceipts() {
  return useQuery({
    queryKey: queryKeys.procurement.receipts(),
      /* O backend devolve envelope paginado; a tela consome array.
         Extrair `.data` aqui mantem o tipo publico do hook. */
    queryFn: async () =>
      (await apiClient.get<{ data: GoodsReceipt[] }>('/procurement/goods-receipts'))
        .data,
  });
}

export function useCreateGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GoodsReceiptInput) =>
      apiClient.post<GoodsReceipt>('/procurement/goods-receipts', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.receipts() });
      qc.invalidateQueries({ queryKey: queryKeys.procurement.orders() });
    },
  });
}

/**
 * Segundo passo: registra o que chegou.
 *
 * `PATCH /procurement/goods-receipts/:id/receive` existia e nenhuma tela
 * chamava — a de recebimentos tentava lancar quantidade recebida na criacao.
 */
export function useReceiveGoods() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ReceiveGoodsInput & { id: string }) =>
      apiClient.patch<GoodsReceipt>(`/procurement/goods-receipts/${id}/receive`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.receipts() });
      qc.invalidateQueries({ queryKey: queryKeys.procurement.orders() });
    },
  });
}

/** Fecha o recebimento e lanca o estoque. */
export function useCompleteGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<GoodsReceipt>(`/procurement/goods-receipts/${id}/complete`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.procurement.receipts() });
      qc.invalidateQueries({ queryKey: queryKeys.procurement.orders() });
    },
  });
}
